/**
 * NosAKDashboard — /nosak/dashboard
 * CEO Control Tower: Board attention, progress gauge, waterfall, actions, matrix.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useNosak } from "@/context/NOSAKContext";
import client from "@/api/client";
import { AlertTriangle, CheckCircle2, TrendingUp, ArrowRight, Target, Activity, DollarSign, Zap, Building2, Shield } from "lucide-react";

const fmtN = (v, u = "") => { if (!v && v !== 0) return "—"; const n = Number(v); if (n >= 1e6) return `${(n/1e6).toFixed(1)}M ${u}`.trim(); if (n >= 1e3) return `${(n/1e3).toFixed(0)}K ${u}`.trim(); return `${n.toLocaleString()} ${u}`.trim(); };
const fmtU = (v) => { if (!v && v !== 0) return "—"; const n = Number(v); if (n >= 1e9) return `$${(n/1e9).toFixed(1)}B`; if (n >= 1e6) return `$${(n/1e6).toFixed(1)}M`; if (n >= 1e3) return `$${(n/1e3).toFixed(0)}K`; return `$${n}`; };
const fmtPct = v => `${Number(v || 0).toFixed(1)}%`;

export default function NosAKDashboard() {
  const { group, subsidiaries, projects, loading, selectedPeriod, periods, setSelectedPeriod } = useNosak();
  const navigate = useNavigate();
  const [insights, setInsights] = useState([]);

  useEffect(() => {
    if (!selectedPeriod) return;
    client.get(`/nosak/insights/${selectedPeriod}`).then(r => setInsights(r.data?.insights || [])).catch(() => {});
  }, [selectedPeriod]);

  if (loading) return <div className="p-8 text-slate-400 text-sm">Loading…</div>;

  const qs = group.qualification_summary || {};
  const fin = group.sustainable_finance || {};
  const scope = group.scope_split || {};
  const projList = (projects.list || []);
  const finReadyProjects = projList.filter(p => ["design","implementation"].includes(p.status));
  const conf = group.data_confidence_avg || 0;
  const targetReduction = 0.30; // 30% by 2030 moderate target
  const progressPct = Math.min(100, (qs.feasible_total || 0) / Math.max(group.total_tco2e || 1, 1) * 100);

  // Waterfall steps
  const waterfallSteps = [
    { label: "Baseline", value: group.total_tco2e || 0, color: "#475569" },
    { label: "Technically Feasible", value: qs.feasible_total || 0, color: "#0891b2" },
    { label: "Creditable", value: qs.creditable_total || 0, color: "#d97706" },
    { label: "Monetisable", value: qs.monetisable_total || 0, color: "#059669" },
    { label: "Net Residual", value: Math.max(0, (group.total_tco2e || 0) - (qs.feasible_total || 0)), color: "#94a3b8" },
  ];
  const maxW = Math.max(...waterfallSteps.map(s => s.value), 1);

  // Board attention items (SSOT-derived)
  const boardItems = [
    conf < 65 && {
      level: "critical", label: "Evidence Quality Below Threshold",
      text: `${conf.toFixed(0)}/100 avg confidence. 3 subsidiaries at estimated level only. VVB validation blocked until ≥65.`,
      action: "Upgrade T1 evidence", path: "/nosak/evidence",
    },
    (group.circularity_score_avg || 0) < 55 && {
      level: "warning", label: "Circularity Gap vs 2028 Target",
      text: `Current: ${(group.circularity_score_avg||0).toFixed(0)}/100. Target: 65/100. EFB/POME valorisation gap: ${fmtN(group.total_tco2e * 0.05, "t/yr")} unrecovered.`,
      action: "Review biomass strategy", path: "/nosak/intelligence",
    },
    {
      level: "opportunity", label: "Green Bond Pre-Engagement Window",
      text: `${finReadyProjects.length} projects finance-ready. ${fmtU(fin.total_capex_pipeline)} pipeline. DFI engagement recommended Q2 2026.`,
      action: "Finance structuring", path: "/nosak/finance",
    },
    {
      level: "opportunity", label: "Carbon Programme Pre-Screening",
      text: `POME methane capture (ACM0010): strong additionality. ${fmtN(qs.monetisable_total, "tCO₂e/yr")} monetisable. DOE pre-screening required.`,
      action: "Carbon markets", path: "/nosak/finance",
    },
  ].filter(Boolean);

  const LEVEL_STYLE = {
    critical:    { bg: "#fef2f2", border: "#fca5a5", dot: "#ef4444", label: "Board" },
    warning:     { bg: "#fffbeb", border: "#fcd34d", dot: "#f59e0b", label: "Attention" },
    opportunity: { bg: "#ecfdf5", border: "#6ee7b7", dot: "#10b981", label: "Opportunity" },
  };

  return (
    <div className="p-5 lg:p-6 bg-[#F8FAFC] min-h-screen space-y-4">

      {/* Header strip */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-black text-slate-900">Executive Dashboard</h1>
          <p className="text-[10px] text-slate-400">{selectedPeriod} · {group.subsidiary_count} subsidiaries · SSOT-driven</p>
        </div>
        <select value={selectedPeriod} onChange={e => setSelectedPeriod(e.target.value)}
          className="text-xs border border-slate-200 rounded-lg px-3 py-1.5 bg-white hidden sm:block" data-testid="period-selector">
          {periods.map(p => <option key={p.period_id} value={p.period_id}>{p.label}</option>)}
        </select>
      </div>

      {/* KPI Banner */}
      <div className="bg-[#0f172a] rounded-xl p-4 text-white" data-testid="executive-kpi-banner">
        <div className="grid grid-cols-4 gap-4 mb-3">
          {[
            ["Total Emissions",    fmtN(group.total_tco2e, "tCO₂e/yr"),                "#f87171"],
            ["Reduction Pipeline", fmtN(group.reduction_potential_tco2e, "tCO₂e/yr"), "#34d399"],
            ["Monetisable",        fmtN(qs.monetisable_total, "tCO₂e/yr"),             "#fbbf24"],
            ["Base Carbon Value",  fmtU(qs.monetisable_total * 18) + "/yr",            "#94a3b8"],
          ].map(([k, v, c]) => (
            <div key={k}>
              <p className="text-[9px] opacity-40 uppercase tracking-wider">{k}</p>
              <p className="text-lg font-black mt-0.5" style={{ color: c }}>{v}</p>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-4 gap-4 pt-3 border-t border-white/10 text-[10px]">
          {[
            { k: "Circularity", v: (group.circularity_score_avg||0).toFixed(0)+"/100", warn: (group.circularity_score_avg||0)<55 },
            { k: "Confidence",  v: conf.toFixed(0)+"/100",                              warn: conf<65 },
            { k: "Finance-Ready", v: `${finReadyProjects.length}/${projects.total||0} projects`, warn: false },
            { k: "ICMA Eligible", v: `${fin.icma_eligible_count||0}/${group.subsidiary_count||0} subs`, warn: false },
          ].map(({ k, v, warn }) => (
            <div key={k}>
              <p className="opacity-30 uppercase tracking-wider text-[8px]">{k}</p>
              <p className={`font-black mt-0.5 ${warn ? "text-amber-400" : "text-slate-300"}`}>{v}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Two-column main content */}
      <div className="grid grid-cols-3 gap-4">

        {/* Left 2/3: Waterfall + Insights */}
        <div className="col-span-2 space-y-4">

          {/* Emissions waterfall */}
          <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-bold text-slate-700">Emissions Reduction Waterfall</p>
              <p className="text-[9px] text-slate-400">Baseline → Feasible → Creditable → Monetisable</p>
            </div>
            <div className="space-y-1.5">
              {waterfallSteps.map(s => {
                const pct = maxW > 0 ? (s.value / maxW) * 100 : 0;
                return (
                  <div key={s.label} className="flex items-center gap-3">
                    <div className="w-28 text-right flex-shrink-0">
                      <p className="text-[9px] font-medium text-slate-600">{s.label}</p>
                    </div>
                    <div className="flex-1 h-6 bg-slate-100 rounded overflow-hidden">
                      <div className="h-full flex items-center pl-2 rounded transition-all"
                        style={{ width: Math.max(pct, 1) + "%", background: s.color }}>
                        <span className="text-[9px] font-bold text-white whitespace-nowrap">
                          {pct > 8 ? fmtN(s.value, "tCO₂e") : ""}
                        </span>
                      </div>
                    </div>
                    {pct <= 8 && (
                      <span className="text-[9px] font-bold text-slate-600 w-20 flex-shrink-0">{fmtN(s.value)}</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Insights + Actions row */}
          <div className="grid grid-cols-2 gap-3">
            {/* Operational Insights */}
            <div className="bg-white border border-slate-100 rounded-xl shadow-sm overflow-hidden">
              <div className="px-4 py-2.5 border-b border-slate-100 bg-slate-50">
                <p className="text-[10px] font-bold text-slate-700">Operational Insights</p>
              </div>
              <div className="p-3 space-y-1.5">
                {insights.slice(0, 4).map((ins, i) => (
                  <div key={i} className="flex items-start gap-2 p-2 rounded-lg bg-slate-50">
                    <div className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0"
                      style={{ background: ins.priority === "High" ? "#dc2626" : ins.priority === "Medium" ? "#d97706" : "#059669" }} />
                    <p className="text-[9px] text-slate-600 leading-relaxed">{ins.text?.slice(0, 120)}…</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Strategic Actions */}
            <div className="bg-white border border-slate-100 rounded-xl shadow-sm overflow-hidden">
              <div className="px-4 py-2.5 border-b border-slate-100 bg-slate-50">
                <p className="text-[10px] font-bold text-slate-700">Strategic Actions</p>
              </div>
              <div className="divide-y divide-slate-50">
                {[
                  { n: 1, text: "Commission POME baseline study", path: "/nosak/finance",     ready: "Ready", color: "#059669" },
                  { n: 2, text: "Upgrade Distilleries to T1",    path: "/nosak/evidence",    ready: "Ready", color: "#059669" },
                  { n: 3, text: "Engage accredited DOE",         path: "/nosak/finance",     ready: "Q2 2026", color: "#d97706" },
                  { n: 4, text: "Execute Circular Packaging",    path: "/nosak/intelligence",ready: "Active", color: "#1d4ed8" },
                  { n: 5, text: "Prepare GBF mandate",           path: "/nosak/finance",     ready: "Q3 2026", color: "#d97706" },
                ].map(a => (
                  <button key={a.n} onClick={() => navigate(a.path)}
                    className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-slate-50 text-left">
                    <span className="text-[9px] font-black text-slate-400 w-4 flex-shrink-0">#{a.n}</span>
                    <p className="text-[10px] font-medium text-slate-700 flex-1 truncate">{a.text}</p>
                    <span className="text-[8px] font-bold px-1.5 py-0.5 rounded flex-shrink-0"
                      style={{ background: a.color + "20", color: a.color }}>{a.ready}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Subsidiary Matrix */}
          <div className="bg-white border border-slate-100 rounded-xl shadow-sm overflow-hidden">
            <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between">
              <p className="text-[10px] font-bold text-slate-700">Subsidiary Performance Matrix</p>
              <button onClick={() => navigate("/nosak/intelligence")} className="text-[9px] text-blue-600 flex items-center gap-1">
                Deep analysis <ArrowRight size={9} />
              </button>
            </div>
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  {["Subsidiary","Emissions","Reduction","Monetisable","Circ.","Conf.","ICMA"].map(h => (
                    <th key={h} className="text-left py-1.5 px-3 text-slate-500 font-semibold text-[10px]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {subsidiaries.map(s => {
                  const q = s.carbon_qualification || {};
                  const c = s.data_confidence_score || 0;
                  return (
                    <tr key={s.sub_id} className="border-b border-slate-50 hover:bg-slate-50">
                      <td className="py-2 px-3 font-medium text-slate-700 max-w-[110px]">
                        <p className="truncate">{s.name}</p>
                        <p className="text-[9px] text-slate-400 capitalize">{(s.division||"").replace(/_/g," ")}</p>
                      </td>
                      <td className="py-2 px-3 text-slate-600 text-[10px]">{fmtN(s.total_tco2e)}</td>
                      <td className="py-2 px-3 text-emerald-700 font-bold text-[10px]">{fmtN(s.reduction_potential_tco2e)}</td>
                      <td className="py-2 px-3 text-amber-700 text-[10px]">{fmtN(q.monetisable||0)}</td>
                      <td className="py-2 px-3">
                        <div className="flex items-center gap-1.5">
                          <div className="w-8 h-1 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: (s.circularity_score||0)+"%", background: (s.circularity_score||0)>=55?"#059669":"#d97706" }}/>
                          </div>
                          <span className="text-[10px] font-bold">{(s.circularity_score||0).toFixed(0)}</span>
                        </div>
                      </td>
                      <td className="py-2 px-3">
                        <span className={`text-[8px] font-bold px-1 py-0.5 rounded ${c>=65?"bg-emerald-50 text-emerald-700":c>=45?"bg-amber-50 text-amber-700":"bg-red-50 text-red-700"}`}>
                          {c>=65?"T2":c>=45?"T1":"Low"}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-center">
                        {s.sustainable_finance?.icma_eligible
                          ? <CheckCircle2 size={11} className="text-emerald-500" />
                          : <span className="text-[9px] text-slate-300">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Right 1/3: Board Attention + Progress */}
        <div className="space-y-3">

          {/* Group Progress Gauge */}
          <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm">
            <p className="text-[10px] font-bold text-slate-700 mb-3">Transition Progress Gauge</p>
            {[
              { label: "Reduction Coverage", val: progressPct, color: "#059669", target: 30, unit: "% of baseline" },
              { label: "Data Confidence",     val: conf,        color: conf>=65?"#059669":"#d97706", target: 65, unit: "/100" },
              { label: "Circularity",         val: group.circularity_score_avg||0, color: "#0891b2", target: 65, unit: "/100" },
              { label: "ICMA Coverage",       val: (fin.icma_eligible_pct||0), color: "#7c3aed", target: 80, unit: "% eligible" },
            ].map(({ label, val, color, target, unit }) => (
              <div key={label} className="mb-2.5">
                <div className="flex justify-between text-[9px] mb-1">
                  <span className="font-medium text-slate-600">{label}</span>
                  <span className="font-black" style={{ color }}>{val.toFixed(0)}{unit.includes("%") ? "%" : unit}</span>
                </div>
                <div className="relative w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: Math.min(100, val)+"%", background: color }} />
                  {/* Target marker */}
                  <div className="absolute top-0 bottom-0 w-0.5 bg-slate-400 opacity-60"
                    style={{ left: Math.min(100, target)+"%", transform: "translateX(-50%)" }} />
                </div>
                <p className="text-[8px] text-slate-400 mt-0.5">Target: {target}{unit.includes("%") ? "%" : ""}</p>
              </div>
            ))}
          </div>

          {/* Board Attention Required */}
          <div className="bg-white border border-slate-100 rounded-xl shadow-sm overflow-hidden">
            <div className="px-3 py-2.5 border-b border-slate-100 flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-red-500" />
              <p className="text-[10px] font-bold text-slate-700">Board Attention Required</p>
            </div>
            <div className="divide-y divide-slate-50">
              {boardItems.map((item, i) => {
                const s = LEVEL_STYLE[item.level];
                return (
                  <button key={i} onClick={() => navigate(item.path)}
                    className="w-full flex items-start gap-2.5 p-3 hover:bg-slate-50 text-left"
                    data-testid={`board-item-${i}`}>
                    <div className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1" style={{ background: s.dot }} />
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className="text-[8px] font-black uppercase" style={{ color: s.dot }}>{s.label}</span>
                        <p className="text-[10px] font-bold text-slate-700 truncate">{item.label}</p>
                      </div>
                      <p className="text-[9px] text-slate-500 leading-relaxed">{item.text?.slice(0, 90)}…</p>
                      <p className="text-[9px] font-bold mt-1 flex items-center gap-1" style={{ color: s.dot }}>
                        {item.action} <ArrowRight size={8} />
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Quick scenario sensitivity */}
          <div className="bg-[#0f172a] rounded-xl p-3 text-white">
            <p className="text-[9px] font-bold uppercase tracking-widest opacity-40 mb-2">Carbon Value Sensitivity</p>
            {[
              ["$10/t (floor)",     fmtU((qs.monetisable_total||0)*10)+"/yr",  "#94a3b8"],
              ["$18/t (base VCM)",  fmtU((qs.monetisable_total||0)*18)+"/yr",  "#34d399"],
              ["$30/t (optimistic)",fmtU((qs.monetisable_total||0)*30)+"/yr",  "#fbbf24"],
            ].map(([k,v,c]) => (
              <div key={k} className="flex justify-between text-[9px] py-1 border-b border-white/10 last:border-0">
                <span className="opacity-50">{k}</span>
                <span className="font-black" style={{ color: c }}>{v}</span>
              </div>
            ))}
            <p className="text-[8px] opacity-30 mt-2">On monetisable tier only. Not bankable until VVB-validated.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
